export const getToken = () => {
  const data = localStorage.getItem("token");
  if (data) return data;
  else return null;
};

/**
 * DB `role.id` → AccessControl codename (from role.codename).
 * Keep in sync with the `role` table.
 */
export const ROLE_ID_TO_CODENAME: Record<string, string> = {
  "1": "admin",
  "2": "cashier",
  "3": "accounting",
  "4": "supervisor",
  "5": "custodian",
  "6": "marketing",
  "7": "clerk",
  "8": "super_admin",
  "9": "accounting_ap",
  "10": "treasury_in_charge",
  "11": "laboratory_head",
  "12": "hr_staff",
  "13": "treasury_assistant",
};

/** Map display names / loose labels → codename. */
const ROLE_NAME_TO_CODENAME: Record<string, string> = {
  admin: "admin",
  "admin manager": "admin",
  cashier: "cashier",
  accounting: "accounting",
  supervisor: "supervisor",
  custodian: "custodian",
  "petty cash custodian": "custodian",
  marketing: "marketing",
  "marketing head": "marketing",
  clerk: "clerk",
  "account receivables clerk": "clerk",
  super_admin: "super_admin",
  "super admin": "super_admin",
  accounting_ap: "accounting_ap",
  "accounting ap": "accounting_ap",
  treasury_in_charge: "treasury_in_charge",
  "treasury-in-charge": "treasury_in_charge",
  "treasury in charge": "treasury_in_charge",
  treasury_assistant: "treasury_assistant",
  "treasury assistant": "treasury_assistant",
  laboratory_head: "laboratory_head",
  "laboratory head": "laboratory_head",
  hr_staff: "hr_staff",
  "hr staff": "hr_staff",
};

/** Resolve a single token (id, codename, or display name) to a codename. */
export function resolveRoleCodename(token: string): string | null {
  const raw = String(token ?? "").trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();

  if (ROLE_ID_TO_CODENAME[raw]) return ROLE_ID_TO_CODENAME[raw];
  if (ROLE_ID_TO_CODENAME[lower]) return ROLE_ID_TO_CODENAME[lower];
  if (ROLE_NAME_TO_CODENAME[lower]) return ROLE_NAME_TO_CODENAME[lower];

  // Already a known-looking codename with underscores
  if (/^[a-z][a-z0-9_]*$/.test(lower)) return lower;

  return null;
}

/** Split + resolve role ids/names into AccessControl codenames. */
export function parseRoleList(
  value: string | string[] | null | undefined
): string[] {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .flatMap((v) => parseRoleList(String(v)))
          .filter(Boolean)
      )
    );
  }
  const raw = String(value).trim();
  if (!raw) return [];
  if (raw.startsWith("[") && raw.endsWith("]")) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parseRoleList(parsed);
    } catch {
      /* fall through */
    }
  }

  const tokens = raw
    .split(/[,|;]/)
    .map((r) => r.trim())
    .filter(Boolean);

  const resolved = tokens
    .map((t) => resolveRoleCodename(t))
    .filter((t): t is string => !!t);

  return Array.from(new Set(resolved));
}

/** Build codenames from login payload (ids and/or names). */
export function rolesFromLoginPayload(data: {
  role_id?: string | number | Array<string | number>;
  role_name?: string | string[];
  role_names?: string | string[];
  roles?: any[];
  role_codename?: string | string[];
}): string[] {
  const fromIds = parseRoleList(
    Array.isArray(data.role_id)
      ? data.role_id.map(String).join(",")
      : data.role_id != null
        ? String(data.role_id)
        : ""
  );

  const nameSource =
    data.role_codename ??
    data.role_names ??
    data.role_name ??
    data.roles ??
    "";

  const fromNames = Array.isArray(nameSource)
    ? parseRoleList(
        nameSource.map((r: any) =>
          typeof r === "string" ? r : r?.codename || r?.name || ""
        )
      )
    : parseRoleList(String(nameSource));

  // Prefer id→codename mapping (authoritative); merge any extra name-based roles
  return Array.from(new Set([...fromIds, ...fromNames]));
}

/** All role codenames for the logged-in user (supports multi-role). */
export function getUserRoles(): string[] {
  const fromRole = parseRoleList(localStorage.getItem("role"));
  if (fromRole.length > 0) return fromRole;
  // Fallback if only role_id was stored (e.g. "8,5")
  return parseRoleList(localStorage.getItem("role_id"));
}

/**
 * Backward-compatible: returns comma-joined role codenames.
 * Prefer getUserRoles() / hasRole() for checks.
 */
export function getUserRole(): string | null {
  const roles = getUserRoles();
  return roles.length > 0 ? roles.join(",") : null;
}

export function getUserRoleIds(): string[] {
  const raw = localStorage.getItem("role_id");
  if (!raw) return [];
  return String(raw)
    .split(/[,|;]/)
    .map((r) => r.trim())
    .filter(Boolean);
}

export function getUserBranch() {
  return localStorage.getItem("branch_id");
}

export function getUserDepartmentId(): string | null {
  return localStorage.getItem("department_id");
}

export function getUserDepartment(): string | null {
  return localStorage.getItem("department");
}

export const getRequester = () => {
  const data = localStorage.getItem("id");
  if (data) return data;
  else return null;
};

export function getAccountName(_section?: string) {
  return localStorage.getItem("full_name") ?? "";
}
